/**
 * PORT-005 §7 / PORT-008 §8.1 — ce que le shell lit AVANT de peindre son menu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POURQUOI CES HOOKS TRAITENT LE 401 EUX-MÊMES
 * ═══════════════════════════════════════════════════════════════════════════
 * `apiFetch` n'a **aucun** intercepteur de 401 (`lib/api.ts`), et c'est heureux :
 * un intercepteur global renverrait vers `/login` — la page du back-office, aux
 * couleurs d'Argalis. Une personne externe n'a rien à y voir. La sortie d'un
 * espace, c'est `/espace`, et rien d'autre.
 *
 * Le 401 n'est donc **pas une erreur** pour ces hooks : c'est un état légitime
 * (« pas de session »), que le layout traduit en redirection. Le distinguer
 * d'une vraie panne réseau est ce qui évite de faire clignoter une page
 * d'erreur devant quelqu'un qui n'est simplement pas connecté.
 */

import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { extractStructuredApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/queryKeys'

import type { PortalBranding, PortalMe } from './usePortalMe.types'

/** Rendu quand le serveur répond 401 : pas de session, pas de panne. */
export const SANS_SESSION = Symbol('portal:sans-session')

/**
 * Une PANNE de `/me` — tout ce qui n'est ni 200 ni 401.
 *
 * ⚠️ Elle porte son statut parce que l'écran d'erreur le CITE
 * (`portal.context.error` attend `{{code}}`) : c'est ce que la personne pourra
 * dire à son organisme. Un `Error` nu ne gardait le statut que dans son texte.
 */
export class ErreurDeLEspace extends Error {
    statut: number
    constructor(statut: number) {
        super(`portal/me: ${statut}`)
        this.statut = statut
    }
}

/** Le code à citer : le statut HTTP, ou `NET` pour une coupure réseau. */
export function codeDeLErreur(erreur: unknown): string {
    return erreur instanceof ErreurDeLEspace ? String(erreur.statut) : 'NET'
}

async function _lireMe(): Promise<PortalMe | typeof SANS_SESSION> {
    const r = await apiFetch('/api/portal/me')
    if (r.status === 401) return SANS_SESSION
    if (!r.ok) throw new ErreurDeLEspace(r.status)
    return (await r.json()) as PortalMe
}

/**
 * L'identité, l'organisme et les contextes de la personne connectée.
 *
 * ⚠️ `retry: false` sur le 401 : réessayer trois fois avant de conclure
 * « pas de session » fait attendre plusieurs secondes devant un écran vide
 * quelqu'un dont le cookie a simplement expiré.
 */
export function usePortalMe() {
    return useQuery({
        queryKey: queryKeys.portal.me(),
        queryFn: _lireMe,
        retry: (echecs, erreur) => echecs < 2 && !(erreur instanceof TypeError),
        staleTime: 30_000,
    })
}

/**
 * L'habillage public de l'organisme du sous-domaine.
 *
 * ⚠️ Il ne remplace jamais `organisation` de `/me` : avant connexion le
 * sous-domaine ne désigne que l'entité RACINE, alors qu'après, l'espace ouvert
 * peut appartenir à une agence qui a ses propres couleurs. Le shell préfère
 * donc `/me` dès qu'il l'a (PORT-008 §8.2).
 */
export function usePortalBranding() {
    return useQuery({
        queryKey: queryKeys.portal.branding(),
        queryFn: async (): Promise<PortalBranding | null> => {
            const r = await apiFetch('/api/portal/branding')
            // Un hôte sans organisme (404) n'est pas une panne : la page se
            // peint en neutre. Un logo de repli serait pire que pas de logo.
            if (!r.ok) return null
            return (await r.json()) as PortalBranding
        },
        staleTime: 5 * 60_000,
        retry: false,
    })
}

/**
 * PORT-005 §5.3 / §8.1 (A12) — le code du refus d'une route de CONTENU dont
 * l'espace a fermé pendant la visite. Une seule chaîne à l'écran pour ce cas :
 * `portal.context.closed`.
 */
export const CODE_ESPACE_FERME = 'PORTAL_CONTEXT_UNAVAILABLE'

/** La réponse dit-elle « l'espace actif a fermé » ? Le corps est lu sur une COPIE. */
export async function estLaFermetureDeLEspace(r: Response): Promise<boolean> {
    if (r.status !== 403) return false
    const corps = await r.clone().json().catch(() => null)
    return extractStructuredApiError(corps).code === CODE_ESPACE_FERME
}

/**
 * A12 — **quand une route de contenu dit « fermé », la coque relit `/me`.**
 *
 * `/me` sert alors `active_context_closed: true` et l'espace fermé en moins ; la
 * coque ouvre la page de choix avec `portal.context.closed`, ou rend l'état vide
 * s'il ne reste rien (`PortalLayout`, `useEntreeAutomatique`). ⛔ Pas de page
 * d'erreur pleine : ce 403 n'est pas une panne, c'est un état.
 *
 * ⚠️ Un AUTRE 403 (`PORTAL_CAPABILITY_REQUIRED` : une section fermée à ce contact)
 * ne relit rien — l'espace reste ouvert, seule la section est refusée.
 *
 * ⏳ **DORMANT, PAS OUBLIÉ** : aucune route de contenu n'est montée à ce jour
 * (PORT-014 les diffère). Chaque lecture de contenu de l'espace passe sa réponse
 * ici ; c'est `lEtatVideEtLEspaceFermeNeTournentPasEnRond.test.tsx` qui prouve la
 * chaîne entière. Rend `true` quand la fermeture a été reconnue.
 */
export function useQuandLEspaceFerme() {
    const queryClient = useQueryClient()
    return useCallback(
        async (r: Response): Promise<boolean> => {
            if (!(await estLaFermetureDeLEspace(r))) return false
            await queryClient.invalidateQueries({ queryKey: queryKeys.portal.me() })
            return true
        },
        [queryClient],
    )
}
